import _ from 'lodash'

/**
 * Remove from a COMPANY settings blob:
 *  - credit processor credentials/ids/keys
 *  - delivery area zones
 *
 *  Only run this on company blobs and not location ones.
 *
 * @param {String,Object} settingsBlob - if it's an object, then it will be mutated
 * @returns cleaned blob as a json string.
 * @throws error if parameter doesn't look like a settings blob or if JSON can't serialize
 */
export function cleanCompanySettingsBlob (settingsBlob) {
  const blob = typeof settingsBlob === 'string'
    ? JSON.parse(settingsBlob)
    : settingsBlob

  if (!blob || blob.entityType !== 'LOCATION_SETTINGS') {
    throw new Error('Not a recognizable settings blob.')
  }

  cleanCreditAndGift(blob)
  cleanDeliveryZones(blob)

  return JSON.stringify(blob)
}

/**
 * Copy the physicalPrinters list from MENU printers in fromBlob to matching
 * menu printers in toBlob.
 *
 * Do not pass string blobs
 * @param {Object} fromBlob - a menu blob object
 * @param {Object} toBlob - a menu whose printers should have fromBlob's physicalPrinters lists.
 */
export function menuCopyPhysicalPrinters (fromBlob, toBlob) {
  if (!fromBlob || !fromBlob.departments) {
    throw new Error('Not a recognizable menu blob.')
  }
  (fromBlob.printers || []).forEach(menuPrinter => {
    const target = (toBlob.printers || []).find(toP => toP.id === menuPrinter.id)
    if (target) {
      target.physicalPrinters = menuPrinter.physicalPrinters
    }
  })
}

/**
 * Remove all '$added' (and $replaced) properties found anywhere within obj.
 * i.e. for when cleaning a location blob to save as a company blob.
 *
 * @param {Object} obj
 */
export function cleanAdded (obj) {
  if (Array.isArray(obj)) {
    obj.forEach(el => {
      cleanAdded(el)
    })
  } else if (obj && typeof obj === 'object') {
    if (obj.$added) {
      delete obj.$added
    }
    if (obj.$replace) {
      delete obj.$replace
    }
    Object.keys(obj).forEach(key => {
      cleanAdded(obj[key])
    })
  }
}

function cleanCreditAndGift (blob) {
  if (blob.creditAndGiftSettings) {
    const creditProcessors = _.get(blob, 'creditAndGiftSettings.accountSettings.processors', {})
    const giftProcessors = _.get(blob, 'creditAndGiftSettings.giftCardProcessor.processors', {})
    const processorGroups = [creditProcessors, giftProcessors]
    const cleanFields = {
      CAYAN: [
        'merchantKey',
        'merchantName',
        'merchantSiteId'
      ],

      MERCURY_PAY: [
        'cardNotPresentMerchantId',
        'cardPresentMerchantId',
        'password'
      ],

      OPEN_EDGE: [
        'accountToken', // credit
        'merchantId' // gift
      ],

      WORLD_PAY_ELEMENT: [
        'acceptorId',
        'accountId',
        'accountToken',
        'terminalId',
        'storeCardId', // gift
        'storeCardPassword' // gift
      ]
    }

    processorGroups.forEach(processors => {
      Object.keys(processors).forEach(processorKey => {
        if (cleanFields[processorKey]) {
          for (let i = 0, len = cleanFields[processorKey].length; i < len; i++) {
            const field = cleanFields[processorKey][i]
            if (processors[processorKey][field]) {
              processors[processorKey][field] = ''
            }
          }
        }
      })
    })
  }
}

function cleanDeliveryZones (blob) {
  if (blob.deliveryArea && blob.deliveryArea.zones) {
    blob.deliveryArea.zones.splice(0)
  }
}
